import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';

interface TaskFiltersErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface TaskFiltersErrorBoundaryProps {
  children: React.ReactNode;
  onRetry?: () => void;
}

export class TaskFiltersErrorBoundary extends React.Component<
  TaskFiltersErrorBoundaryProps,
  TaskFiltersErrorBoundaryState
> {
  constructor(props: TaskFiltersErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): TaskFiltersErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('TaskFilters Error Boundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
    this.props.onRetry?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <View style={styles.errorContainer}>
          <SVG_COMPONENTS.X_CIRCLE width={24} height={24} color={ADMIN_COLORS.ERROR} />
          <Text style={styles.errorTitle}>Filter Error</Text>
          <Text style={styles.errorMessage}>
            Unable to load task filters. Please try again.
          </Text>
          <TouchableOpacity style={styles.retryButton} onPress={this.handleRetry}>
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  errorContainer: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.HEADER_BACKGROUND,
    borderBottomWidth: 2,
    borderBottomColor: ADMIN_COLORS.DATA_TABLE.BORDER,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    padding: 20,
    alignItems: 'center',
    marginBottom: 14,
  },
  errorTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.ERROR,
    marginTop: 8,
    marginBottom: 4,
  },
  errorMessage: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    marginBottom: 12,
  },
  retryButton: {
    backgroundColor: ADMIN_COLORS.ERROR,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '500',
  },
});
